import React, { Component, createRef } from 'react';
import ModalModule from '../modal';
import UI from '../ui';
import Utils from '../utils';

const { Modal, ModalButtonActionType, ModalContext } = ModalModule;
const { WrappedFileUpload } = UI;
const { Logger, Communication } = Utils;

export default class ImportCostCodesModal extends Component {
	constructor(props) {
		super(props);
		this.fileUploader = createRef();
		this.state = {
			isImportDisabled: true,
			fileName: '',
			selectedFile: null,
		};
	}

	componentDidMount() {
		const { modalId } = this.props;
		const { showError, closeModal } = this.context;

		this.fileReader = new FileReader();

		// process file on load
		this.fileReader.onload = event => {
			const csv = event.target.result;
			const textRows = csv.split(/\r\n|\n/);
			const parsedText = [];

			for (const row of textRows) {
				const splitRow = row.split(',');

				for (const item of splitRow) {
					const codeToken = item.trim();

					if (codeToken !== '') {
						parsedText.push(codeToken);
					}
				}
			}

			if (!this.validateInput(parsedText)) {
				closeModal(modalId);

				return showError({
					data: {
						title: 'Import Error',
						description:
							'There was a problem importing your cost codes. Please verify your CSV or text file and try again.',
						handleType: 'custom',
					},
				});
			}
			this.importCostCodes(parsedText);
		};

		// handle load error
		this.fileReader.onerror = event => {
			if (event.target.error.name === 'NotReadableError') {
				showError({
					data: {
						title: 'Import Error',
						description:
							'There was a problem importing your cost codes. Please verify your CSV or text file and try again.',
						handleType: 'custom',
					},
				});
			}
		};
	}

	checkIsFileTypeValid = ({ name }) =>
		name.indexOf('.csv') !== -1 || name.indexOf('.txt') !== -1;

	validateInput = parsedArray => {
		const {
			data: { remainingCodes },
		} = this.props;

		// check if user input is empty
		if (parsedArray.length === 0) {
			return false;
		}
		// check if more cost codes are being added than the remaining cost code limit
		else if (parsedArray.length > remainingCodes) {
			return false;
		} else {
			return parsedArray.reduce((isValid, code) => {
				if (isValid) {
					if (code.length > 30) {
						// return if code length exceeds 30 characters
						return false;
					}
				}
				return isValid;
			}, true);
		}
	};

	// on upload, process data using HTML5 FileReader API and set to state
	onUpload = () => {
		const { selectedFile } = this.state;

		// read file into memory as UTF-8
		this.fileReader.readAsText(selectedFile);
	};

	importCostCodes = async data => {
		const {
			data: { customerId, onSuccess },
			modalId,
		} = this.props;
		const { closeModal, showError } = this.context;
		const response = await Communication.addCostCodes(data, customerId);

		if (Communication.isSuccessful(response)) {
			onSuccess();
			Logger.logToServer({
				category: 'import_costcode',
				costcode_count: data.length,
			});
		} else {
			showError({ response });
		}

		closeModal(modalId, ModalButtonActionType.Ok);
	};

	onImportButtonClick = () => {
		this.setState({ isImportDisabled: true }, this.onUpload);
	};

	onSelect = ({ files }) => {
		const { showError } = this.context;
		const isFileTypeValid = this.checkIsFileTypeValid(files[0]);

		if (!isFileTypeValid) {
			showError({
				data: {
					title: 'Invalid file type',
					description:
						'Please select a CSV or text file containing cost codes of 30 characters or less separated by commas.',
					handleType: 'custom',
				},
			});
		}

		this.setState({
			isImportDisabled: isFileTypeValid === false,
			selectedFile: isFileTypeValid ? files[0] : null,
			fileName: isFileTypeValid ? files[0].name : '',
		});
	};

	render() {
		const { modalId } = this.props;
		const { isImportDisabled, fileName } = this.state;

		const primaryButton = {
			type: ModalButtonActionType.Ok,
			text: 'Import',
			onClick: this.onImportButtonClick,
			disabled: isImportDisabled,
			dataqa: 'import-cost-codes-import-button',
		};

		const secondaryButton = {
			type: ModalButtonActionType.Cancel,
			text: 'Cancel',
			dataqa: 'import-cost-codes-cancel-button',
		};

		return (
			<Modal
				modalId={modalId}
				className="common-cost-codes import-cost-codes-modal"
				header="Import Cost Codes"
				primaryButton={primaryButton}
				secondaryButton={secondaryButton}
			>
				<div className="body">
					<p>
						Click <b>Select File</b> to select a CSV or text file and click{' '}
						<b>Import</b>.
					</p>
					<p>Duplicate Cost Codes will not be created.</p>
					<div className="upload-container">
						<WrappedFileUpload
							ref={this.fileUploader}
							name="importCostCodes"
							className="transparent"
							chooseLabel="Select File"
							accept=".txt, .csv"
							mode="basic"
							// This will trigger uploadHandler; But its needed
							// Otherwise it will change the label for the upload button
							// And replace it with the filename instead
							auto
							dataqa="importcostcodes"
							onSelect={this.onSelect}
							// user custom uploadHandler method since there is
							// a designated "Import" button to trigger upload.
							customUpload
							uploadHandler={() => {
								if (this.fileUploader.current) {
									// when user clicks "Select File" button after a file
									// has been selected, system clears the selected file
									// to allow user to reselect.

									if (fileName !== '') {
										this.fileUploader.current.clear();
									}

									this.setState({
										fileName: '',
										isImportDisabled: true,
									});
								}
							}}
						/>
						<p>{fileName}</p>
					</div>
				</div>
			</Modal>
		);
	}
}

ImportCostCodesModal.contextType = ModalContext;
